import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { ThemeProvider } from "next-themes";
import ThemeToggle from "@/components/ThemeToggle";
import { Link, NavLink } from "react-router";

const ColorSwatch = ({ name, color }: { name: string; color: string }) => (
  <div className="flex flex-col gap-2">
    <div className={cn("size-24 rounded-md border", color)} />
    <span className="text-xs font-medium">{name}</span>
  </div>
);

const ComponentSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="mb-8">
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent className="flex flex-wrap gap-4">{children}</CardContent>
  </Card>
);

const RenderColorPalette = () => {
  const colors = [
    { name: "Background", color: "bg-background" },
    { name: "Foreground", color: "bg-foreground" },
    { name: "Primary", color: "bg-primary" },
    { name: "Secondary", color: "bg-secondary" },
    { name: "Accent", color: "bg-accent" },
    { name: "Muted", color: "bg-muted" },
    { name: "Destructive", color: "bg-destructive" },
    { name: "Chart 1", color: "bg-[--color-chart-1]" },
    { name: "Chart 2", color: "bg-[--color-chart-2]" },
    { name: "Chart 3", color: "bg-[--color-chart-3]" },
    { name: "Chart 4", color: "bg-[--color-chart-4]" },
    { name: "Chart 5", color: "bg-[--color-chart-5]" },
    { name: "Border", color: "bg-border" },
    { name: "Input", color: "bg-input" },
    { name: "Ring", color: "bg-ring" },
    { name: "Sidebar", color: "bg-[--color-sidebar]" },
    { name: "Sidebar Foreground", color: "bg-[--color-sidebar-foreground]" },
    { name: "Sidebar Primary", color: "bg-[--color-sidebar-primary]" },
    { name: "Sidebar Accent", color: "bg-[--color-sidebar-accent]" },
    { name: "Sidebar Border", color: "bg-[--color-sidebar-border]" },
    { name: "Sidebar Ring", color: "bg-[--color-sidebar-ring]" },
  ];

  return (
    <ComponentSection title="Color Palette">
      <div className="grid grid-cols-8 gap-4 mx-auto">
        {colors.map((color) => (
          <ColorSwatch
            key={color.color}
            name={color.name}
            color={color.color}
          />
        ))}
      </div>
    </ComponentSection>
  );
};

const InfoSection = () => (
  <ComponentSection title="Info">
    {/* Tailwind v4 docs */}
    <NavLink
      to="https://tailwindcss.com/docs/styling-with-utility-classes"
      target="_blank"
      className="text-sm text-muted-foreground"
    >
      Tailwind v4 docs
    </NavLink>
    <NavLink
      to="https://ui.shadcn.com/docs/components/button"
      target="_blank"
      className="text-sm text-muted-foreground"
    >
      Shadcn docs
    </NavLink>
    <NavLink
      to="https://react.dev/reference/react"
      target="_blank"
      className="text-sm text-muted-foreground"
    >
      React 19 docs
    </NavLink>
  </ComponentSection>
);

export default function Dev() {
  return (
    <div className="container py-8 mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Component Library</h1>
        <ThemeToggle />
      </div>

      <InfoSection />

      <RenderColorPalette />

      <ComponentSection title="Typography">
        <div className="flex flex-col gap-2">
          <h1 className="text-4xl font-bold">Heading 1</h1>
          <h2 className="text-3xl font-bold">Heading 2</h2>
          <h3 className="text-2xl font-bold">Heading 3</h3>
          <h4 className="text-xl font-bold">Heading 4</h4>
          <h5 className="text-lg font-bold">Heading 5</h5>
          <h6 className="text-base font-bold">Heading 6</h6>
        </div>
      </ComponentSection>

      <ComponentSection title="Text Examples">
        <div className="flex flex-col gap-2">
          <p className="bg-background p-2 border border-border rounded-md">
            <span className="text-lg mx-2">text-lg with bg-background</span>
            <span className="text-base mx-2">text-base with bg-background</span>
            <span className="text-sm mx-2">text-sm with bg-background</span>
            <span className="text-xs mx-2">text-xs with bg-background</span>
          </p>
          <p className="text-lg bg-muted p-2 border border-border rounded-md">
            <span className="text-lg mx-2">text-lg with bg-muted</span>
            <span className="text-base mx-2">text-base with bg-muted</span>
            <span className="text-sm mx-2">text-sm with bg-muted</span>
            <span className="text-xs mx-2">text-xs with bg-muted</span>
          </p>
          <p className="text-lg bg-secondary p-2 border border-border rounded-md">
            <span className="text-lg mx-2">text-lg with bg-secondary</span>
            <span className="text-base mx-2">text-base with bg-secondary</span>
            <span className="text-sm mx-2">text-sm with bg-secondary</span>
            <span className="text-xs mx-2">text-xs with bg-secondary</span>
          </p>
          <p className="text-lg bg-accent p-2 border border-border rounded-md">
            <span className="text-lg mx-2">text-lg with bg-accent</span>
            <span className="text-base mx-2">text-base with bg-accent</span>
            <span className="text-sm mx-2">text-sm with bg-accent</span>
            <span className="text-xs mx-2">text-xs with bg-accent</span>
          </p>
        </div>
      </ComponentSection>

      <ComponentSection title="Buttons">
        <Button variant="default">Default</Button>
        <Button variant="destructive">Destructive</Button>
        <Button variant="outline">Outline</Button>
        <Button variant="secondary">Secondary</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="link">Link</Button>
      </ComponentSection>

      <ComponentSection title="Badges">
        <Badge variant="default">Default</Badge>
        <Badge variant="secondary">Secondary</Badge>
        <Badge variant="destructive">Destructive</Badge>
        <Badge variant="outline">Outline</Badge>
      </ComponentSection>

      <ComponentSection title="Form Elements">
        <div className="flex flex-col gap-2 w-full max-w-sm">
          <Label htmlFor="email">Email</Label>
          <Input id="email" placeholder="Enter your email" />
          <Textarea placeholder="Your message" />
          <div className="flex items-center gap-2">
            <Switch id="airplane-mode" />
            <Label htmlFor="airplane-mode">Airplane Mode</Label>
          </div>
        </div>
      </ComponentSection>

      <ComponentSection title="Progress & Loading">
        <div className="w-full max-w-sm space-y-4">
          <Progress value={60} />
          <div className="flex gap-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="h-4 w-[200px]" />
            </div>
          </div>
        </div>
      </ComponentSection>

      <ComponentSection title="Sheet">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline">Open Sheet</Button>
          </SheetTrigger>
          <SheetContent>
            <div className="p-4">
              <h3 className="text-lg font-medium">Sheet Content</h3>
              <p className="mt-2">This is an example of the Sheet component.</p>
            </div>
          </SheetContent>
        </Sheet>
      </ComponentSection>
    </div>
  );
}
